import { repositoryFactory } from '@data';
import { applyScoreDecay } from '@core/algorithms';

/**
 * Decay Calculator Service
 * Manages score decay calculations and application
 */
export class DecayCalculator {
  constructor() {
    this.progressRepository = repositoryFactory.getProgressRepository();
  }

  /**
   * Apply decay to a single question's progress
   */
  applyDecayToQuestion(questionProgress) {
    return applyScoreDecay(questionProgress);
  }

  /**
   * Apply decay to all questions for a user
   * Uses PRD applyScoreDecay algorithm
   */
  async applyDecayToAllQuestions(userId, courseId) {
    const progress = await this.progressRepository.getUserProgress(userId, courseId);
    const courseRepo = repositoryFactory.getCourseRepository();
    const course = await courseRepo.getCourseById(courseId);
    
    if (!progress || !course) {
      throw new Error(`Progress or course not found for user: ${userId}`);
    }

    let questionsDecayed = 0;
    const decayResults = [];

    // Build sections data for decay algorithm
    const allSections = course.sections.map(section => ({
      sectionId: section.id,
      index: section.index,
      score: progress.getSectionProgress(section.id)?.currentScore || 0,
    }));

    // Apply decay to each question
    for (const [questionId, questionProgress] of Object.entries(progress.questionProgress)) {
      // Skip if never attempted
      if (!questionProgress.timesShown || questionProgress.timesShown === 0) {
        continue;
      }

      const originalScore = questionProgress.currentScore || 0;
      
      // Apply decay algorithm with section context for recency protection
      const updatedProgress = {
        ...questionProgress,
        score: originalScore,
        sectionId: questionProgress.sectionId,
        lastStudied: questionProgress.lastShown,
      };
      
      const decayedProgress = applyScoreDecay(updatedProgress, allSections);
      
      // Check if decay actually occurred
      if (decayedProgress.score !== originalScore) {
        questionsDecayed++;
        const decayAmount = originalScore - decayedProgress.score;
        
        decayResults.push({
          questionId,
          originalScore,
          newScore: decayedProgress.score,
          decayAmount,
        });

        // Update question progress with decayed score
        const updatedQuestionProgress = {
          ...questionProgress,
          currentScore: decayedProgress.score,
          lastDecayAppliedAt: Date.now(),
        };

        await this.progressRepository.updateQuestionProgress(
          userId,
          courseId,
          questionId,
          updatedQuestionProgress
        );
      }
    }

    return {
      questionsDecayed,
      decayResults,
    };
  }

  /**
   * Apply decay to specific section's questions
   */
  async applyDecayToSection(userId, courseId, sectionId) {
    const progress = await this.progressRepository.getUserProgress(userId, courseId);
    const courseRepo = repositoryFactory.getCourseRepository();
    const questions = await courseRepo.getQuestionsForSection(courseId, sectionId);

    if (!progress) {
      throw new Error(`Progress not found for user: ${userId}`);
    }

    let questionsDecayed = 0;
    const decayResults = [];

    // Apply decay to section questions only
    for (const question of questions) {
      const questionProgress = progress.getQuestionProgress(question.id);
      
      if (!questionProgress) {
        continue;
      }

      const originalScore = questionProgress.score;
      const updatedProgress = this.applyDecayToQuestion(questionProgress);
      
      if (updatedProgress.score !== originalScore) {
        questionsDecayed++;
        decayResults.push({
          questionId: question.id,
          originalScore,
          newScore: updatedProgress.score,
          decayAmount: originalScore - updatedProgress.score,
        });

        // Update question progress
        await this.progressRepository.updateQuestionProgress(
          userId,
          courseId,
          question.id,
          updatedProgress
        );
      }
    }

    return {
      sectionId,
      questionsDecayed,
      decayResults,
    };
  }

  /**
   * Check which questions need decay applied
   */
  async getQuestionsNeedingDecay(userId, courseId) {
    const progress = await this.progressRepository.getUserProgress(userId, courseId);
    
    if (!progress) {
      return [];
    }

    const questionsNeedingDecay = [];
    const now = Date.now();
    const oneDayMs = 24 * 60 * 60 * 1000;

    for (const [questionId, questionProgress] of Object.entries(progress.questionProgress)) {
      // Skip if never attempted
      if (!questionProgress.lastAttemptAt) {
        continue;
      }

      // Check if decay was applied recently (within 24 hours)
      if (questionProgress.lastDecayAppliedAt) {
        const hoursSinceDecay = (now - new Date(questionProgress.lastDecayAppliedAt)) / (1000 * 60 * 60);
        if (hoursSinceDecay < 24) {
          continue; // Skip, decay was applied recently
        }
      }

      // Check if enough time has passed since last attempt
      const daysSinceAttempt = (now - new Date(questionProgress.lastAttemptAt)) / oneDayMs;
      
      if (daysSinceAttempt >= 1) {
        questionsNeedingDecay.push({
          questionId,
          currentScore: questionProgress.score,
          daysSinceAttempt,
          lastAttemptAt: questionProgress.lastAttemptAt,
        });
      }
    }

    return questionsNeedingDecay;
  }

  /**
   * Get decay statistics for user
   */
  async getDecayStatistics(userId, courseId) {
    const progress = await this.progressRepository.getUserProgress(userId, courseId);
    
    if (!progress) {
      return null;
    }

    const stats = {
      totalQuestions: 0,
      questionsWithDecay: 0,
      averageScore: 0,
      averageDaysSinceAttempt: 0,
      questionsNeedingReview: [],
    };

    const scores = [];
    const daysSinceAttempts = [];
    const now = Date.now();
    const oneDayMs = 24 * 60 * 60 * 1000;

    for (const [questionId, questionProgress] of Object.entries(progress.questionProgress)) {
      if (questionProgress.attempts === 0) {
        continue;
      }

      stats.totalQuestions++;
      scores.push(questionProgress.score);

      if (questionProgress.lastAttemptAt) {
        const daysSince = (now - new Date(questionProgress.lastAttemptAt)) / oneDayMs;
        daysSinceAttempts.push(daysSince);

        if (daysSince >= 1) {
          stats.questionsWithDecay++;
        }

        // Flag questions that need review (score dropped below 70%)
        if (questionProgress.score < 70 && questionProgress.correctAttempts > 0) {
          stats.questionsNeedingReview.push({
            questionId,
            score: questionProgress.score,
            daysSinceAttempt: daysSince,
          });
        }
      }
    }

    if (scores.length > 0) {
      stats.averageScore = scores.reduce((a, b) => a + b, 0) / scores.length;
    }

    if (daysSinceAttempts.length > 0) {
      stats.averageDaysSinceAttempt = daysSinceAttempts.reduce((a, b) => a + b, 0) / daysSinceAttempts.length;
    }

    return stats;
  }

  /**
   * Calculate in-session spacing
   * Ensures questions are spaced 8-15 questions apart within a session
   * Implements HamTestOnline spaced repetition: don't repeat within N questions
   */
  calculateInSessionGap(sessionQuestions, targetQuestion, minGap = 8, maxGap = 15) {
    if (!sessionQuestions || sessionQuestions.length === 0) {
      return { canAsk: true, gap: 0 };
    }

    // Find the position of this question in recent session
    const lastIndex = sessionQuestions.length - 1;
    let lastOccurrence = -1;

    // Search backward through session questions to find last occurrence
    for (let i = lastIndex; i >= 0; i--) {
      const sessionQuestion = sessionQuestions[i];
      const questionId = sessionQuestion.questionId || sessionQuestion.id;
      
      if (questionId === targetQuestion.id) {
        lastOccurrence = i;
        break;
      }
    }

    // If not in session or well-spaced, can ask
    if (lastOccurrence === -1) {
      return { canAsk: true, gap: sessionQuestions.length, lastPosition: -1 };
    }

    const gap = sessionQuestions.length - lastOccurrence - 1;
    const canAsk = gap >= minGap;

    return {
      canAsk,
      gap,
      lastPosition: lastOccurrence,
      minGapRequired: minGap,
      nextAvailablePosition: lastOccurrence + minGap,
    };
  }

  /**
   * Get next available question that respects in-session spacing
   * Filters candidates to only those that can be asked based on spacing rules
   */
  filterByInSessionSpacing(candidates, sessionQuestions, minGap = 8) {
    if (!sessionQuestions || sessionQuestions.length === 0) {
      return candidates;
    }

    const sessionQuestionIds = new Set(
      sessionQuestions.map(q => q.questionId || q.id)
    );

    return candidates.filter(candidate => {
      const spacing = this.calculateInSessionGap(sessionQuestions, candidate, minGap);
      return spacing.canAsk;
    });
  }

  /**
   * Get spacing statistics for a session
   */
  getSessionSpacingStats(sessionQuestions) {
    if (!sessionQuestions || sessionQuestions.length === 0) {
      return {
        totalQuestions: 0,
        uniqueQuestions: 0,
        repeatedQuestions: [],
      };
    }

    const questionCounts = {};
    const repeated = [];

    for (const sessionQuestion of sessionQuestions) {
      const qId = sessionQuestion.questionId || sessionQuestion.id;
      questionCounts[qId] = (questionCounts[qId] || 0) + 1;

      if (questionCounts[qId] > 1) {
        repeated.push({
          questionId: qId,
          occurrences: questionCounts[qId],
        });
      }
    }

    return {
      totalQuestions: sessionQuestions.length,
      uniqueQuestions: Object.keys(questionCounts).length,
      repeatedQuestions: repeated,
      repetitionRate: (repeated.length / Object.keys(questionCounts).length) * 100,
    };
  }
}

// Export singleton instance
export const decayCalculator = new DecayCalculator();
